import { JSX } from 'react';
import { PartialDeep } from 'type-fest';

import {
  BenefitMenuIcon,
  HomeMenuIcon,
  NewsMenuIcon,
  PortalTheme,
  SitesMenuIcon,
  WalletMenuIcon,
} from '@frontend-libs/portal';

import {
  PortalConfigurationModel,
  PortalPublicContentModel,
} from '@portal-clients/configuration';
import { SiteUnitListModel } from '@portal-clients/site-unit';
import { UserModel } from '@portal-clients/user';

import { tApp } from '@frontend/locales';
import { routes } from '@frontend/utils';

export interface MenuActionsProps {
  siteUnits: SiteUnitListModel[] | undefined;
  locationPath: string;
  theme: PortalTheme;
  user: UserModel | undefined;
  newsLicense: boolean | undefined;
  vouchersLicense: boolean | undefined;
  stampCardsLicense: boolean | undefined;
  config: PortalConfigurationModel | undefined;
  content: PartialDeep<PortalPublicContentModel> | undefined;
  isNewsEnabled: boolean | undefined;
  newsCount: number;
  voucherCount: number;
  isHomeEnabled: boolean | undefined;
}

export interface MenuAction {
  disabled: boolean;
  isActive: boolean;
  route: string;
  navigateTo: string;
  title: string;
  ariaLabelSuffix: string;
  count: number | undefined;
  getIcon: () => JSX.Element;
}

export const MenuActions = (props: MenuActionsProps): MenuAction[] => {
  const menuActions: MenuAction[] = [];

  if (props.isHomeEnabled) {
    menuActions.push({
      disabled: false,
      isActive:
        props.user !== undefined &&
        isActionActive(props.locationPath, routes.home),
      route: routes.home,
      navigateTo: routes.home,
      title: props.content?.homeTitle
        ? props.content?.homeTitle
        : tApp.headers.home(),
      ariaLabelSuffix: 'home',
      count: undefined,
      getIcon: () => {
        return (
          <HomeMenuIcon
            stroke={
              isActionActive(props.locationPath, routes.home)
                ? props.theme.palette.primary.main
                : props.theme.palette.text.secondary
            }
            filled={isActionActive(props.locationPath, routes.home)}
          />
        );
      },
    });
  }

  const singleSiteUnit =
    props.siteUnits?.length === 1 ? props.siteUnits[0] : undefined;

  menuActions.push({
    disabled:
      (!props.siteUnits || props.siteUnits?.length === 0) && !props.user,
    isActive: isActionActive(props.locationPath, routes.siteUnits.sites),
    route: routes.siteUnits.sites,
    navigateTo: singleSiteUnit
      ? routes.siteUnits.site(singleSiteUnit.id, true)
      : routes.siteUnits.sites,
    title: props.content?.siteTitle
      ? props.content?.siteTitle
      : tApp.headers.siteUnits(),
    ariaLabelSuffix: 'sites',
    count: undefined,
    getIcon: () => {
      return (
        <SitesMenuIcon
          stroke={
            isActionActive(props.locationPath, routes.siteUnits.sites)
              ? props.theme.palette.primary.main
              : props.theme.palette.text.secondary
          }
          filled={isActionActive(props.locationPath, routes.siteUnits.sites)}
        />
      );
    },
  });

  menuActions.push({
    disabled: !props.user,
    isActive:
      props.user !== undefined &&
      isActionActive(props.locationPath, routes.wallet.list()),
    route: routes.wallet.list(),
    navigateTo: routes.wallet.list(),
    title: props.content?.walletTitle
      ? props.content?.walletTitle
      : tApp.headers.wallet(),
    ariaLabelSuffix: 'wallet',
    count: undefined,
    getIcon: () => {
      return (
        <WalletMenuIcon
          stroke={
            props.user !== undefined &&
            isActionActive(props.locationPath, routes.wallet.list())
              ? props.theme.palette.primary.main
              : props.theme.palette.text.secondary
          }
          filled={
            props.user !== undefined &&
            isActionActive(props.locationPath, routes.wallet.list())
          }
        />
      );
    },
  });

  if (
    (props.vouchersLicense && props.config?.features?.voucher?.enabled) ||
    (props.stampCardsLicense && props.config?.features?.stampCard?.enabled) ||
    props.config?.features?.rule?.enabled
  ) {
    menuActions.push({
      disabled: !props.user,
      isActive:
        props.user !== undefined &&
        isActionActive(props.locationPath, routes.benefits),
      route: routes.benefits,
      navigateTo: routes.benefits,
      title: props.content?.benefitTitle
        ? props.content?.benefitTitle
        : tApp.headers.benefits(),
      ariaLabelSuffix: 'benefits',
      count: props.voucherCount,
      getIcon: () => {
        return (
          <BenefitMenuIcon
            stroke={
              isActionActive(props.locationPath, routes.benefits)
                ? props.theme.palette.primary.main
                : props.theme.palette.text.secondary
            }
            filled={isActionActive(props.locationPath, routes.benefits)}
          />
        );
      },
    });
  }

  if (props.isNewsEnabled && props.newsLicense) {
    menuActions.push({
      disabled: false,
      isActive:
        props.user !== undefined &&
        isActionActive(props.locationPath, routes.news()),
      route: routes.news(),
      navigateTo: routes.news(),
      title: props.content?.newsTitle
        ? props.content?.newsTitle
        : tApp.headers.news(),
      ariaLabelSuffix: 'news',
      count: props.newsCount,
      getIcon: () => {
        return (
          <NewsMenuIcon
            stroke={
              isActionActive(props.locationPath, routes.news())
                ? props.theme.palette.primary.main
                : props.theme.palette.text.secondary
            }
            filled={isActionActive(props.locationPath, routes.news())}
          />
        );
      },
    });
  }

  return menuActions;
};

const isActionActive = (locationPath: string, route: string) => {
  if (route === '/') {
    return locationPath === route;
  }

  return locationPath.startsWith(route);
};
